import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { Box, Checkbox, FormControlLabel, MenuItem } from '@mui/material'
import { toast } from '@/helpers'
import { routes } from '@/router'
import { UserProfile } from '@/types'
import { crmErrorMessage } from './crm-error'
import { sessionButtonSx } from '../session-button'
import { SessionField, SessionScreen } from '../session-screen'
import { CRM_UFS } from './profile.constants'
import {
  flowProgress,
  nextProfileStep,
  previousProfileStep,
  setNoCrmChoice,
} from './profile-steps'
import { ProfileStepFrame } from './profile-step-frame'
import { saveProfile } from './save-profile'

export function ProfileCrmPage() {
  return (
    <ProfileStepFrame step="crm">
      {(user) => <CrmStep user={user} />}
    </ProfileStepFrame>
  )
}

function CrmStep({ user }: { user: UserProfile }) {
  const navigate = useNavigate()
  const [crm, setCrm] = useState(user.profile?.crm ?? '')
  const [uf, setUf] = useState(user.profile?.crm_uf ?? '')
  const [noCrm, setNoCrm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [crmError, setCrmError] = useState<string | null>(null)

  const isValid = noCrm || (crm.length >= 4 && Boolean(uf))
  const previous = previousProfileStep('crm', user)

  async function continueCrm() {
    if (!isValid || saving) return

    if (noCrm) {
      setNoCrmChoice(true)
      navigate(routes.session.profile.situation)
      return
    }

    setSaving(true)
    setCrmError(null)
    try {
      setNoCrmChoice(false)
      const refreshed = await saveProfile({
        practice_area: 'Medicina',
        occupation: 'doctor',
        crm,
        crm_uf: uf,
      })
      const step = nextProfileStep(refreshed)
      navigate(step ? routes.session.profile[step] : routes.home)
    } catch (error) {
      const message = crmErrorMessage(error)
      if (message) {
        setCrmError(message)
      } else {
        toast.error('Não foi possível conferir o CRM agora. Tente de novo.')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <SessionScreen
      title="Informe o seu CRM"
      progress={flowProgress('crm', user)}
      onBack={() =>
        navigate(
          previous
            ? routes.session.profile[previous]
            : routes.session.profile.area,
        )
      }
      footer={
        <LoadingButton
          size="large"
          fullWidth
          loading={saving}
          disabled={!isValid}
          onClick={continueCrm}
          sx={sessionButtonSx()}
        >
          {saving ? 'Conferindo CRM...' : 'Continuar'}
        </LoadingButton>
      }
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
        }}
      >
        <SessionField
          id="crm"
          label="Número do CRM"
          value={crm}
          placeholder="123456"
          disabled={noCrm}
          autoFocus
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 7 } }}
          errorText={crmError ?? undefined}
          onChange={(event) => {
            setCrmError(null)
            setCrm(event.target.value.replace(/\D/g, '').slice(0, 7))
          }}
        />
        <SessionField
          id="crm-uf"
          label="UF do CRM"
          select
          value={uf}
          disabled={noCrm}
          onChange={(event) => setUf(event.target.value)}
        >
          <MenuItem value="">Selecione</MenuItem>
          {CRM_UFS.map((state) => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </SessionField>
      </Box>
      <FormControlLabel
        sx={{ width: 'fit-content' }}
        control={
          <Checkbox
            checked={noCrm}
            onChange={(event) => {
              const checked = event.target.checked
              setNoCrm(checked)
              if (checked) {
                setCrm('')
                setUf('')
              }
            }}
          />
        }
        label="Não tenho CRM"
      />
    </SessionScreen>
  )
}
